import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Checkbox, Form, Input, Segmented, Typography } from 'antd';

import { DataState, Skeleton, deriveDataState } from '../loading';
import RecordBadge from '../common/RecordBadge';
import { cardChain, indexCardsById, indexElementSetsById, mergeChainElements } from '../../utils/cards';
import { apiErrorCode, apiErrorMessage } from '../../utils/apiErrors';
import { NO_TEXT_ASSIST } from '../../utils/fieldAssist';
import { normalizeId } from '../../utils/ids';
import { slugify } from '../../utils/strings';
import { packageSourceCards } from '../../services/packageSource';

const { Text } = Typography;

/*
 * Which of a bundle's cards can use a card a person already has (KPR-1816, the
 * web half of keepr-api KPR-1809; keepr-api SCHEMA.md "A card that can use a
 * card you have"). Every packaged card is Always added, the default, or Can
 * use a card you have: a shared key and a list of the elements any card that
 * fills it must have.
 *
 * What the bundle itself uses is derived by the build, so the web cannot know
 * it first: a package's first dialog says so in one line, and from the first
 * build on `report.requirements` names those elements, checked and locked,
 * matched on the key (ruled on KPR-1816, 2026-11-02). All this section sends is
 * the publisher's extras.
 *
 * The marks a rebuild starts from carry source ids. An admin reads them in
 * `build.requirements`, a manager of the source in `markedCards` (KPR-1876).
 * Neither is ever matched by a card's name.
 */

/** A contract's ceiling, the derived elements included (keepr-api packageRequirements MAX_CONTRACT). */
export const MAX_CONTRACT = 25;

const KEY_RE = /^[a-z0-9][a-z0-9-]{0,59}$/;
const ELEMENT_ID_RE = /^[a-z0-9]{8}$/;
const HEX = /^[0-9a-fA-F]{24}$/;

const idOf = value => String(normalizeId(value) || '').toLowerCase();
const uniqueSorted = list => [...new Set(list || [])].sort();

/**
 * The cards a bundle carries: those chosen and their parents (every card of
 * the collection when none is chosen), as ids — or null when the cards are
 * not known yet (nothing is held back).
 */
export function bundleCardIds(cards, chosen) {
	if (!Array.isArray(cards) || !cards.length) return null;
	const byId = new Map(cards.map(c => [normalizeId(c._id), c]));
	const picked = Array.isArray(chosen) && chosen.length ? chosen.map(normalizeId) : [...byId.keys()];
	const out = new Set();
	picked.forEach((id) => {
		for (let at = id, hops = 0; at && byId.has(at) && !out.has(at) && hops < 20; hops++) {
			out.add(at);
			at = byId.get(at).parentCardId ? normalizeId(byId.get(at).parentCardId) : null;
		}
	});
	return out;
}

/** The marks a rebuild starts from, in the form's shape; `build.requirements` and `markedCards` both read here. */
export function marksOf(stored) {
	return (Array.isArray(stored) ? stored : [])
		.filter(mark => mark && mark.cardId && mark.key)
		.map(mark => ({
			cardId: idOf(mark.cardId),
			key: mark.key,
			contract: Array.isArray(mark.contract) ? [...mark.contract] : [],
			...(mark.missing ? { missing: true } : {}),
		}));
}

/** The list `requirements` takes, the form's rows as they stand. Never `missing`, which the API does not take. */
export function requirementsBody(rows) {
	return (Array.isArray(rows) ? rows : []).map(row => ({
		cardId: row.cardId,
		key: String(row.key || '').trim(),
		contract: [...new Set(row.contract || [])],
	}));
}

/** What a first build sends about its marks: nothing at all when no card is marked. */
export function requirementsBuildBody(rows) {
	const body = requirementsBody(rows);
	return body.length ? { requirements: body } : {};
}

const sameMark = (a, b) => a.key === b.key && JSON.stringify(uniqueSorted(a.contract)) === JSON.stringify(uniqueSorted(b.contract));

/**
 * What a rebuild sends about its marks: only a change, so a rebuild left alone
 * posts what it always did. `requirements` replaces the marking whole, so a
 * change sends every mark that stays, and `[]` takes the last one off.
 */
export function requirementsRebuildBody(rows, stored) {
	const now = requirementsBody(rows);
	const before = requirementsBody(marksOf(stored));
	const same = now.length === before.length && now.every((row) => {
		const match = before.find(mark => mark.cardId === row.cardId);
		return Boolean(match) && sameMark(row, match);
	});
	return same ? {} : { requirements: now };
}

/** The element names a build found the bundle uses for this key: what is locked. */
export function lockedNames(report, key) {
	const rows = Array.isArray(report?.requirements) ? report.requirements : [];
	const found = rows.find(row => row && row.key === key);
	return Array.isArray(found?.used) ? found.used : [];
}

/** The key a card suggests: from its own key, or its name; never one another mark holds. */
export function suggestedKey(card, taken = []) {
	const base = slugify(card?.key || card?.name).slice(0, 60).replace(/-+$/, '') || 'card';
	if (!taken.includes(base)) return base;
	for (let n = 2; n < 100; n++) {
		const next = `${base.slice(0, 57).replace(/-+$/, '')}-${n}`;
		if (!taken.includes(next)) return next;
	}
	return base;
}

/**
 * What is wrong with the marks, or null: `{ cardId, sentence }`, the mark to
 * flag and what to say. `nameOf(cardId)` says a card's name.
 */
export function requirementProblem(rows, nameOf = () => null) {
	const list = Array.isArray(rows) ? rows : [];
	const name = row => nameOf(row.cardId) || 'A card';
	const bad = list.find(row => !KEY_RE.test(String(row.key || '').trim()));
	if (bad) return { cardId: bad.cardId, sentence: `${name(bad)} needs a key of lowercase letters, digits and dashes, at most 60, starting with a letter or digit.` };
	const seen = new Map();
	for (const row of list) {
		const key = String(row.key).trim();
		if (seen.has(key)) return { cardId: row.cardId, sentence: `${name(seen.get(key))} and ${name(row)} use the same key. Each card needs its own.` };
		seen.set(key, row);
	}
	return null;
}

/**
 * A build's refusal of the marks as plain text, or null for any other error.
 * The API's sentence names what to change; the place it hands a caller
 * (`where`) and a source id in its words are not for a person.
 */
export function requirementsRefusal(error) {
	if (apiErrorCode(error) !== 'invalid_package') return null;
	const data = error?.response?.data;
	const about = /^requirements/.test(String(data?.where || '')) || data?.key === 'requirements';
	return about ? apiErrorMessage(error).replace(/\s*\([0-9a-f]{24}\)/gi, '').trim() : null;
}

/**
 * A card's elements as an item gets them: its chain, parent-first and merged
 * by name, attached sets folded in. Only those with an id can be ticked: an
 * id is what `contract` takes.
 */
export function chainElements(cardId, cards, sets) {
	const chain = cardChain(indexCardsById(cards), cardId, indexElementSetsById(sets));
	return mergeChainElements(chain).filter(element => ELEMENT_ID_RE.test(String(element?.id || '')));
}

const elementLabel = element => element?.label?.singular || (typeof element?.label === 'string' && element.label) || element?.name || '';

/**
 * What each requirement took from a build, one line each, from `report.requirements`
 * (`{ key, card, used, added }`, by name). `cards` give the elements' labels,
 * and a name is said as it is when none does.
 */
export function requirementReportLines(report, cards = []) {
	const rows = Array.isArray(report?.requirements) ? report.requirements : [];
	const labels = new Map();
	(Array.isArray(cards) ? cards : []).forEach(card => (card?.elements || []).forEach((element) => {
		if (element?.name && !labels.has(element.name)) labels.set(element.name, elementLabel(element));
	}));
	const words = names => names.map(name => labels.get(name) || name).join(', ');
	return rows.filter(row => row && row.key).map((row) => {
		const used = Array.isArray(row.used) ? row.used : [];
		const added = Array.isArray(row.added) ? row.added : [];
		const parts = [
			used.length ? `locks ${words(used)}` : null,
			added.length ? `also asks for ${words(added)}` : null,
		].filter(Boolean);
		return `${row.card || row.key} (${row.key})${parts.length ? ` ${parts.join(', and ')}` : ''}.`;
	});
}

/** The source's cards and sets (a dialog's own read): `{ cards, sets, loading, error, loaded, reload }`. */
function useSourceCards(collectionId) {
	const [state, setState] = useState({ cards: [], sets: [], loading: false, error: null, loaded: false });
	// The read in flight, cancelled by the next one, so another collection's cards are never marked.
	const cancel = useRef(() => {});
	const load = useCallback(() => {
		cancel.current();
		let live = true;
		cancel.current = () => { live = false; };
		if (!collectionId) {
			setState({ cards: [], sets: [], loading: false, error: null, loaded: false });
			return cancel.current;
		}
		setState({ cards: [], sets: [], loading: true, error: null, loaded: false });
		packageSourceCards(collectionId)
			.then(({ cards, sets }) => { if (live) setState({ cards, sets, loading: false, error: null, loaded: true }); })
			.catch((error) => { if (live) setState(prev => ({ ...prev, loading: false, error })); });
		return cancel.current;
	}, [collectionId]);
	useEffect(() => load(), [load]);
	return { ...state, reload: load };
}

const ALWAYS = 'always';
const CAN_USE = 'can-use';
const CHOICES = [{ value: ALWAYS, label: 'Always added' }, { value: CAN_USE, label: 'Can use a card you have' }];

/** One card's elements: what the build locked, checked and fixed, and what the publisher ticks. */
function ContractList({ card, elements, locked, contract, onChange }) {
	const lockedSet = new Set(locked);
	// A name a build locked that this list cannot draw (it has no id, or a read fell short) is still locked.
	const unlisted = locked.filter(name => !elements.some(element => element.name === name));
	const known = elements.filter(element => !lockedSet.has(element.name) && contract.includes(element.id)).length;
	const count = locked.length + known;
	const full = count >= MAX_CONTRACT;
	const toggle = (id, on) => onChange(on ? [...contract, id] : contract.filter(other => other !== id));
	const gone = contract.filter(id => !elements.some(element => element.id === id)).length;
	return (
		<div className="package-requirements__contract" role="group" aria-label={`Elements ${card.name} needs`}>
			<span className="micro-label">Elements it needs</span>
			<Text type="secondary" className="admin-packages__hint">
				{locked.length ? 'The bundle uses the locked ones, so any card that fills it must have them.' : 'keepr adds and locks the elements the bundle uses when it builds.'}
			</Text>
			<ul className="package-requirements__elements">
				{unlisted.map(name => <li key={`locked-${name}`}><Checkbox checked disabled>{name}</Checkbox></li>)}
				{elements.map((element) => {
					const isLocked = lockedSet.has(element.name);
					const ticked = isLocked || contract.includes(element.id);
					return (
						<li key={element.id}>
							<Checkbox checked={ticked} disabled={isLocked || (full && !ticked)} onChange={e => toggle(element.id, e.target.checked)}>{elementLabel(element)}</Checkbox>
						</li>
					);
				})}
			</ul>
			<Text type="secondary" className="admin-packages__hint">
				{full ? `${count} of ${MAX_CONTRACT}, the most a card can need. Untick one to add another.` : `${count} of at most ${MAX_CONTRACT}.`}
			</Text>
			{gone ? <Text type="secondary" className="admin-packages__hint">{gone === 1 ? 'One element you chose has' : `${gone} elements you chose have`} left the card; the next build says so.</Text> : null}
		</div>
	);
}

/** One packaged card: its switch and, for Can use, its key and elements. */
function RequirementRow({ card, mark, stored, sources, report, problem, onSet, onDrop, taken }) {
	const storedMark = stored.find(row => row.cardId === idOf(card._id));
	const elements = useMemo(() => chainElements(card._id, sources.cards, sources.sets), [card, sources.cards, sources.sets]);
	const choose = (value) => {
		if (value === ALWAYS) onDrop(idOf(card._id));
		else onSet(storedMark ? { ...storedMark } : { cardId: idOf(card._id), key: suggestedKey(card, taken), contract: [] });
	};
	return (
		<div className="package-requirements__card">
			<RecordBadge kind="card" record={{ icon: card.icon, color: card.color, name: card.name }} title={card.name} size="sm" />
			<Segmented
				className="package-requirements__switch"
				block
				options={CHOICES}
				value={mark ? CAN_USE : ALWAYS}
				onChange={choose}
				aria-label={`How ${card.name} is added`}
			/>
			{mark ? (
				<>
					<label className="package-requirements__key">
						<span>Requirement key</span>
						<Input
							{...NO_TEXT_ASSIST}
							value={mark.key}
							status={problem ? 'error' : undefined}
							maxLength={60}
							aria-label={`Requirement key for ${card.name}`}
							onChange={e => onSet({ ...mark, key: e.target.value })}
						/>
					</label>
					<Text type="secondary" className="admin-packages__hint">Bundles that use the same key find the same card.</Text>
					<ContractList
						card={card}
						elements={elements}
						// Locks follow the key the build used, whatever the key is being changed to.
						locked={lockedNames(report, storedMark?.key)}
						contract={mark.contract}
						onChange={contract => onSet({ ...mark, contract })}
					/>
				</>
			) : null}
		</div>
	);
}

/** A mark on a card that has left the collection: it says so, and comes off by a person's press, never by itself. */
function GoneRow({ mark, name, onDrop }) {
	return (
		<div className="package-requirements__card">
			<Text strong>{name || mark.key}</Text>
			<Text type="secondary" className="admin-packages__hint">
				This card has left the collection, so a rebuild is refused while it is marked. Restore the card, or take its mark off.
			</Text>
			<Button onClick={() => onDrop(mark.cardId)} aria-label={`Take the mark off ${name || mark.key}`}>Take the mark off</Button>
		</div>
	);
}

/** The rows, as a form control over `[{ cardId, key, contract, missing? }]`. */
function RequirementRows({ value, onChange, cards, stored, sources, report, problemRow }) {
	const rows = useMemo(() => (Array.isArray(value) ? value : []), [value]);
	const has = id => rows.find(row => row.cardId === id);
	const set = next => onChange(has(next.cardId) ? rows.map(row => (row.cardId === next.cardId ? next : row)) : [...rows, next]);
	const drop = id => onChange(rows.filter(row => row.cardId !== id));
	const taken = rows.map(row => row.key);
	const shown = new Set(cards.map(card => idOf(card._id)));
	const reportRows = Array.isArray(report?.requirements) ? report.requirements : [];
	return (
		<>
			{cards.map(card => (
				<RequirementRow
					key={idOf(card._id)}
					card={card}
					mark={has(idOf(card._id))}
					stored={stored}
					sources={sources}
					report={report}
					problem={problemRow === idOf(card._id)}
					onSet={set}
					onDrop={drop}
					taken={taken}
				/>
			))}
			{rows.filter(row => !shown.has(row.cardId)).map(row => (
				<GoneRow key={row.cardId} mark={row} name={reportRows.find(r => r?.key === row.key)?.card} onDrop={drop} />
			))}
		</>
	);
}

/**
 * The section of a build or rebuild dialog (KPR-1816). One field in the
 * enclosing antd Form, `requirements`, which the dialog fills with
 * {@link marksOf} on a rebuild; it then sends {@link requirementsBuildBody} or
 * {@link requirementsRebuildBody}. The rows are read from the form's store,
 * not from what validation answers: the field is not drawn until the cards
 * have been read, and a failed read must leave the marks as they were.
 *
 * `chosen`: the card ids the build names; empty or null is every card the
 * collection owns, except for a `card` package, which waits for its card.
 * `rebuild`: this package has been built, so its marks stand even when their
 * card has moved. `partial`: the caller knows the package carries only some of
 * the source's cards but not which (a publisher's rebuild: `buildView` gives a
 * count, and a card is never matched by name).
 */
export default function RequirementFields({ form, collectionId, kind, chosen = null, stored = null, report = null, rebuild = false, partial = false }) {
	const id = collectionId && HEX.test(String(normalizeId(collectionId))) ? normalizeId(collectionId) : null;
	const eligible = kind === 'card' || kind === 'bundle';
	const sources = useSourceCards(eligible ? id : null);
	// `preserve`: a rebuild's marks are in the form before the field is drawn.
	const marks = Form.useWatch('requirements', { form, preserve: true });
	const rows = useMemo(() => (Array.isArray(marks) ? marks : []), [marks]);
	const storedRows = useMemo(() => marksOf(stored), [stored]);

	const chosenIds = useMemo(() => [].concat(chosen || []).filter(Boolean), [chosen]);
	const owned = useMemo(() => sources.cards.filter(card => card.scope === 'collection' && normalizeId(card.collection_id) === id), [sources.cards, id]);
	const cards = useMemo(() => {
		if (kind === 'card' && !chosenIds.length) return [];
		const carried = bundleCardIds(owned, chosenIds);
		// A mark stands in the list on a rebuild, whatever else changed.
		const marked = new Set(rows.map(row => row.cardId));
		return owned.filter(card => (carried && carried.has(normalizeId(card._id))) || (rebuild && marked.has(idOf(card._id))));
	}, [owned, kind, chosenIds, rows, rebuild]);

	// A first build's marks live and die with what it carries: a card taken out
	// of the selection takes its mark with it, as does the end of a read.
	useEffect(() => {
		if (rebuild || !sources.loaded) return;
		const kept = new Set(cards.map(card => idOf(card._id)));
		const left = rows.filter(row => kept.has(row.cardId));
		if (left.length !== rows.length) form.setFieldsValue({ requirements: left });
	}, [rebuild, sources.loaded, cards, rows, form]);

	if (!eligible || !id) return null;

	const nameOf = cardId => owned.find(card => idOf(card._id) === cardId)?.name || null;
	const problem = sources.loaded ? requirementProblem(rows, nameOf) : null;
	const isEmpty = sources.loaded && !cards.length && !rows.length;

	return (
		<div className="package-requirements">
			<span className="micro-label">Can use a card you have</span>
			<Text type="secondary" className="admin-packages__hint">
				Someone who already keeps this kind of record can point the bundle at their own card, instead of getting a second one.
			</Text>
			{partial ? (
				<Text type="secondary" className="admin-packages__hint">
					This package carries only some of the collection's cards. Marking one it does not carry is refused when it rebuilds.
				</Text>
			) : null}
			<DataState
				state={deriveDataState({ loading: sources.loading, error: sources.error, hasData: sources.loaded, isEmpty })}
				hasData={sources.loaded}
				isEmpty={isEmpty}
				skeleton={<Skeleton variant="list-row" count={2} />}
				empty={<Text type="secondary" className="admin-packages__hint">{kind === 'card' ? 'Choose the card first.' : 'This collection has no cards of its own to mark.'}</Text>}
				onRetry={sources.reload}
				label="its cards"
			>
				<Form.Item
					name="requirements"
					className="package-requirements__field"
					rules={[{ validator: (_, value) => {
						const found = requirementProblem(value, nameOf);
						return found ? Promise.reject(new Error(found.sentence)) : Promise.resolve();
					} }]}
				>
					<RequirementRows cards={cards} stored={storedRows} sources={sources} report={report} problemRow={problem?.cardId} />
				</Form.Item>
			</DataState>
		</div>
	);
}
